import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Search, Calendar, User, FileText, Download, Save, Stethoscope } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface ImplantRecord {
  id: string;
  patient_name: string;
  implant_confirmed_date: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  doctor_notes: string;
  created_at: string;
  updated_at: string;
  notes_updated_at: string | null;
}

const DoctorDashboard = () => {
  const { signOut, doctorProfile } = useAuth();
  const navigate = useNavigate();
  const [implants, setImplants] = useState<ImplantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImplant, setSelectedImplant] = useState<ImplantRecord | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchImplants();
  }, []);

  const fetchImplants = async () => {
    try {
      const { data, error } = await supabase
        .from('implant_records')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImplants(data || []);
    } catch (error) {
      console.error('Error fetching implants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleDownload = async (implant: ImplantRecord) => {
    try {
      const { data, error } = await supabase.storage
        .from('dicom-files')
        .download(implant.storage_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = implant.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleViewDetails = (implant: ImplantRecord) => {
    setSelectedImplant(implant);
    setEditingNotes(implant.doctor_notes || '');
  };

  const handleSaveNotes = async () => {
    if (!selectedImplant) return;

    setSavingNotes(true);
    try {
      const { error } = await supabase
        .from('implant_records')
        .update({ doctor_notes: editingNotes })
        .eq('id', selectedImplant.id);

      if (error) throw error;

      await fetchImplants();
      setSelectedImplant({ ...selectedImplant, doctor_notes: editingNotes });
      alert('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const filteredImplants = implants.filter(implant =>
    implant.patient_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getImplantsByDate = () => {
    const dateMap = new Map<string, ImplantRecord[]>();

    filteredImplants.forEach(implant => {
      const date = implant.implant_confirmed_date;
      if (!dateMap.has(date)) {
        dateMap.set(date, []);
      }
      dateMap.get(date)?.push(implant);
    });

    return Array.from(dateMap.entries())
      .map(([date, patients]) => ({ date, patients, count: patients.length }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const implantsByDate = getImplantsByDate();

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (selectedImplant) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white shadow-md">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <button
              onClick={() => setSelectedImplant(null)}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              Back to List
            </button>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Logged in as</p>
                <p className="font-medium text-gray-900">{doctorProfile?.full_name}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="text-red-600 hover:text-red-700"
                title="Sign Out"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Patient File Details</h2>

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Patient Name</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedImplant.patient_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Implant Confirmed Date</label>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(selectedImplant.implant_confirmed_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">File Name</label>
                  <p className="text-gray-900">{selectedImplant.file_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">File Size</label>
                  <p className="text-gray-900">{formatFileSize(selectedImplant.file_size)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Upload Date</label>
                  <p className="text-gray-900">{formatDate(selectedImplant.created_at)}</p>
                </div>
                {selectedImplant.notes_updated_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Notes Last Updated</label>
                    <p className="text-gray-900">{formatDate(selectedImplant.notes_updated_at)}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Doctor Notes</label>
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:bg-gray-300"
                >
                  <Save size={16} />
                  {savingNotes ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
              <textarea
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows={10}
                placeholder="Add clinical notes, observations, or treatment details..."
              />
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleDownload(selectedImplant)}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 text-lg font-semibold"
              >
                <Download size={32} />
                Download DICOM File
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Stethoscope className="text-green-600" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Doctor Admin</h1>
              <p className="text-sm text-gray-600">Patient Files Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-600">Logged in as</p>
              <p className="font-medium text-gray-900">{doctorProfile?.full_name}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-red-600 hover:text-red-700 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {implantsByDate.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Implants by Date</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {implantsByDate.map(({ date, count }) => (
                <div
                  key={date}
                  className="bg-white rounded-lg shadow-md p-5 border-l-4 border-green-600 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="text-green-600" size={24} />
                    <div className="flex-1">
                      <p className="text-lg font-bold text-gray-900">{formatDate(date)}</p>
                      <p className="text-sm text-gray-600">
                        {count} {count === 1 ? 'patient' : 'patients'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-center bg-green-50 rounded-full w-12 h-12">
                    <span className="text-2xl font-bold text-green-600">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Patient Files</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by patient name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredImplants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No patient files found
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredImplants.map((implant) => (
                <div
                  key={implant.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleViewDetails(implant)}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <User className="text-green-600" size={20} />
                    <h3 className="font-semibold text-gray-900">{implant.patient_name}</h3>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{formatDate(implant.implant_confirmed_date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText size={16} />
                      <span className="truncate">{implant.file_name}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Uploaded: {formatDate(implant.created_at)}
                    </div>
                    {implant.doctor_notes && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-green-600 font-medium">Has Notes</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DoctorDashboard;
